import { TacticOptionsProps } from 'screens/Schedule/FrequencySection/EditContentAccountsModal/Types'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { AccountToggle } from 'screens/Schedule/AccountToggle/AccountToggle'
import { InitiateOauthTrigger } from 'components/ConnectIntegrationTrigger/InitiateOauthTrigger'
import { useConnectAccountPopup } from 'components/Onboarding/useConnectAccountPopup'
import { ButtonProps } from '@react-types/button'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useButton } from '@react-aria/button'
import { ProviderIcon } from 'blaze-ui/icons/ProviderIcon'
import { PLATFORM_DISPLAY_NAMES } from 'utils/platformDisplayNames'
import { forwardRef } from 'react'
import { useObjectRef } from '@react-aria/utils'
import { useQueryClient } from 'react-query'
import { scheduleKeys } from 'hooks/queries/schedules/scheduleKeys'
import { Text } from 'blaze-ui/Text/Text'
import Styles from './TacticOptions.module.scss'
import { useProviders } from './useProviders'

const displayNames = {
  ...PLATFORM_DISPLAY_NAMES,
  [ExternalIntegrationEnum.GoogleBusinessProfile]: 'Google Profile',
}

interface ProviderButtonProps extends Omit<ButtonProps, 'children'> {
  provider: ExternalIntegrationEnum
}

const ProviderButton = forwardRef<HTMLButtonElement, ProviderButtonProps>(({ provider, ...props }, fRef) => {
  const ref = useObjectRef(fRef)
  const { buttonProps } = useButton(props, ref)

  return (
    <button {...buttonProps} ref={ref} className={Styles.providerButton}>
      <ProviderIcon provider={provider} size={20} />
      <Text variant="secondary">Connect {displayNames[provider]}</Text>
    </button>
  )
})

export const TacticOptions = ({
  tactic,
  selectedAccounts,
  onSelectionChange,
  onProviderConnect,
}: TacticOptionsProps) => {
  const providers = useProviders(tactic)
  const queryClient = useQueryClient()
  const { connect } = useConnectAccountPopup({ isSingleSelect: true })

  const { data } = useExternalIntegrations({
    select: (data) => ({
      external_integrations: data.external_integrations.filter((integration) =>
        providers.includes(integration.provider),
      ),
    }),
  })

  const integrations = data?.external_integrations || []
  const integratedProviders = integrations.map((integration) => integration.provider)

  return (
    <ul>
      {integrations.map((integration) => (
        <li key={integration.id} className={Styles.option}>
          <AccountToggle
            size="small"
            integration={integration}
            isSelected={selectedAccounts.includes(integration.id)}
            onChange={(isSelected) => {
              const currentSelection = new Set(selectedAccounts)
              if (isSelected) {
                currentSelection.add(integration.id)
              } else {
                currentSelection.delete(integration.id)
              }

              return onSelectionChange([...currentSelection])
            }}
            name={`${integration.id}`}
          />
        </li>
      ))}
      {providers
        .filter((provider) => !integratedProviders.includes(provider))
        .map((provider) => (
          <li key={provider} className={Styles.option}>
            <InitiateOauthTrigger
              provider={provider}
              popUp
              onInitiatedOauth={(popUp, options) =>
                connect(popUp, {
                  ...options,
                  onConnect: async (externalIntegrations) => {
                    const selection = new Set([
                      ...selectedAccounts,
                      ...externalIntegrations.map((integration) => integration.id),
                    ])
                    await queryClient.invalidateQueries(scheduleKeys.details())
                    return onSelectionChange([...selection])
                  },
                })
              }
              onPress={() => onProviderConnect(provider)}
            >
              <ProviderButton provider={provider} />
            </InitiateOauthTrigger>
          </li>
        ))}
    </ul>
  )
}
